import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { OrderRow, OrderStatus } from "@/pages/admin/orders/columns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { orderStatusEnum } from "@/server/db/schema/orders";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";

interface EditOrderStatusDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  order: OrderRow | null;
  onSuccess?: () => void;
}

export default function EditOrderStatusDialog({
  isOpen,
  onOpenChange,
  order,
  onSuccess,
}: EditOrderStatusDialogProps) {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>(undefined);
  const utils = trpc.useUtils();

  useEffect(() => {
    if (order) {
      setSelectedStatus(order.orderStatus);
    }
  }, [order]);

  const updateOrderStatusMutation = trpc.orders.updateOrder.useMutation({
    onSuccess: () => {
      toast.success(t("admin.adminOrders.editStatusDialog.messages.success"));
      utils.orders.listOrders.invalidate(); // Invalidate the list to refetch
      if (onSuccess) onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(t("admin.adminOrders.editStatusDialog.messages.error"), {
        description: error.message,
      });
    },
  });

  const handleSubmit = () => {
    if (!order || !selectedStatus) return;
    updateOrderStatusMutation.mutate({
      id: order.id,
      orderStatus: selectedStatus,
    });
  };

  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("admin.adminOrders.editStatusDialog.title", { orderNumber: order.orderNumber })}</DialogTitle>
          <DialogDescription>{t("admin.adminOrders.editStatusDialog.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="orderStatusSelect" className="text-sm font-medium">
              {t("admin.adminOrders.editStatusDialog.newStatusLabel")}
            </label>
            <Select
              value={selectedStatus}
              onValueChange={(value) => setSelectedStatus(value as OrderStatus)}
            >
              <SelectTrigger id="orderStatusSelect" className="w-full mt-1">
                <SelectValue placeholder={t("admin.adminOrders.editStatusDialog.statusPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {orderStatusEnum.enumValues.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`enums.orderStatus.${status}`, status.replace(/_/g, " "))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("admin.adminOrders.editStatusDialog.currentStatusInfo", { currentStatus: t(`enums.orderStatus.${order.orderStatus}`)})}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateOrderStatusMutation.isPending}>
            {t("common.actions.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedStatus || selectedStatus === order.orderStatus || updateOrderStatusMutation.isPending}>
            {updateOrderStatusMutation.isPending ? t("common.actions.saving") : t("common.actions.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}